import { MouseEvent, MutableRefObject } from 'react'
import { Variable } from '@microservices/wiskey-react-components'
import { GridColDef, GridRenderCellParams } from '@microservices/wiskey-react-table-component'
import { GridApiPro } from '@microservices/wiskey-react-table-component/dist/DataGrid/DataGridV7/models/gridApiPro'

import { useTimeLineCreateOrEdit } from '@pages/TimeLineCreateOrEdit/hooks'

import { VariableValue } from '@components/Variables/types'

import { TIME_LINE_MODE } from '@constants'

import { EmbeddedObject } from '@gantt/components/GanttCreateOrEdit'

import { getRSqlBySearch } from '../helpers/SAFiltersToRSQL'

import { ActionRow } from './action'
import { ObjectDataRecord, ObjectDataValue, POSTObjectDataFilter } from './api'
import { FormCommand } from './command'
import { AutocompleteOption } from './common'
import { ConfiguredColumn } from './views'

export type ObjectY = {
  // Код объекта
  objectCode?: string
  // Название объекта
  objectTitle?: string
  // Данные по объекту
  object?: AutocompleteOption
  // Параметры команды для загрузки данных по объекту
  getDataCommand: FormCommand
  // Ключевое поле объекта Y, по которому будет соединяться объект X
  keyField: AutocompleteOption | null
  // Предварительная фильтрация объекта Y
  preFilter?: string | null
  // Колонки для отображения
  columns: ConfiguredColumn[]
  // Действия
  actions: ActionRow[]
  entityId?: number
}

export type ObjectX = {
  // Данные по объекту
  object?: AutocompleteOption
  // Параметры команды для загрузки данных по объекту
  getDataCommand: FormCommand
  // Поле из модели объекта, обозначающее начало интервала
  startDateField: AutocompleteOption | null
  // Поле из модели объекта, обозначающее конец интервала
  endDateField: AutocompleteOption | null
  // Конфигурация ссылки на ObjectY
  linkToObjectY?: {
    keyField: string | null
    pathArray: EmbeddedObject[]
  }
  // Конфигурация ячейки
  cell: TimeLineGridXCell
  // Колонки для отображения после основных колонок объекта X
  columns: ConfiguredColumn[]
  actions: ActionRow[]
  totalsRows: TotalRow[]
  entityId?: number

  linkToColumnsObject?: {
    keyField: string | null
    pathArray: EmbeddedObject[]
  }
  fieldFromColumnsObjectToLink: {
    keyField: string | null
    pathArray: EmbeddedObject[]
  }
  fieldFromObjectYToLink: {
    keyField: string | null
    pathArray: EmbeddedObject[]
  }

  columnsObject?: {
    object?: AutocompleteOption
    command: FormCommand
    headerBindType?: AutocompleteOption | null
    headerField?: {
      keyField: string | null
      pathArray: EmbeddedObject[]
    }
    headerScript?: string
  }
}

export const enum GroupTitleType {
  JS = 'JS',
  JSX = 'JSX',
  FIELD = 'FIELD',
}

export const enum MainObjectTypeEnum {
  Y = 'Y',
  X = 'X',
}

export type MainObjectType = {
  objectType: MainObjectTypeEnum
  groupTitleType?: AutocompleteOption<GroupTitleType> | null
  groupTitleScript?: string
  groupTitleDisplayField?: EmbeddedObject[] | null
  groupTitleDisplayFieldKey?: string
  groupField?: EmbeddedObject[] | null
  groupFieldKey?: string
}

export const enum INCREMENT_TYPE {
  MINUTE = 'MINUTE',
  HOUR = 'HOUR',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export type TimeLineGridInterval = {
  increment: INCREMENT_TYPE
  dateRange?: [string, string]
  showFilter: boolean
}

export type TimeLineGridConfig = {
  id?: number
  title: string
  code: string
  jsonConfig?: string
  timeInterval?: TimeLineGridInterval
  objectY: ObjectY
  objectX: ObjectX
  mainObject: MainObjectType
  showUpdateTimeLineDataButton: boolean
  actions: ActionRow[]
  variables: VariableValue[]
  showDayOfWeek: boolean
  searchAssistant?: AutocompleteOption | null
  filterObjectYByObjectX?: boolean
  filterColumnsObjectByObjectX?: boolean
  mode: AutocompleteOption<TIME_LINE_MODE> | null
}

export type TimeLineGroupedData = {
  yElement: ObjectDataRecord
  xArray: ObjectDataRecord[]
}

export type DateIntervalKey = {
  // Здесь будут лежать ключи, сформированные на основе интервала дат в виде [start;end]
  [key in `[${string};${string}]`]?: ObjectDataRecord[]
}

export type TimeLineGridDataRecord = {
  id: string
  yElement: ObjectDataRecord
  value?: unknown
  groupId?: string
  title?: string
  columnsInfo: Record<string, ObjectDataValue>
} & Record<string, ObjectDataValue>

export type TimeLineGridXCell = {
  bindType: AutocompleteOption | null
  value: string
  minWidth: number
  minHeight: number
}

export type TimeLineGridColumn = {
  columnType?: COLUMN_TYPE
} & GridColDef

export type TimeLineCreateOrEditPageContextType = ReturnType<typeof useTimeLineCreateOrEdit>

export type DataGridRefType = MutableRefObject<GridApiPro>

export type TotalRow = {
  commands: FormCommand[]
  bindType: AutocompleteOption | null
  script: string
  title: string
  id?: string
}

export const enum COLUMN_TYPE {
  OBJECT_Y = 'objectY',
  OBJECT_X = 'objectX',
  DATE_INTERVAL = 'dateInterval',
}

export type TimelineOnSearchEventSrcObj = {
  startDate: string
  endDate: string
  objectYSAFilters: POSTObjectDataFilter
  objectXSAFilters: POSTObjectDataFilter
  getRSqlBySearch: typeof getRSqlBySearch
}

export type TimelineCellMove = {
  item: ObjectDataRecord
  sourceRowId: string
  sourceColumnId: string
  targetRowId: string
  targetColumnId: string
  source: TimelineDragCell
  target: TimelineDragCell
}

export type TimelineDragCell = {
  cellId: string
  rowId: string
  columnId: string
  columnInfo?: ObjectDataRecord
  htmlElement?: HTMLElement
  isSelected?: boolean
  rowData?: ObjectDataRecord
}

export type TimelineDragCellsRefState = {
  source: TimelineDragCell[]
  target: (TimelineDragCell | null)[]
  dragStartCell: TimelineDragCell | null
}

export type TimelineGhostCell = {
  relX: number
  relY: number
  width: number
  height: number
  rowData: ObjectDataRecord
  columnId: string
  rowId: string
  events: ObjectDataRecord[]
}

export type TimelineCellItemDragState = {
  item: ObjectDataRecord
  sourceCellId: string
  sourceRowId: string
  sourceColumnId: string
  isBulk: boolean
  anchorOffsetX: number
  anchorOffsetY: number
  ghostCells: TimelineGhostCell[]
  initialMouseX: number
  initialMouseY: number
}

export type TimelineCellContext = {
  objectYTotalCount: number
  selectedCells: Record<string, SelectedCell>
  isSelectionDragging: boolean
  hasSelectionDragged: boolean
  dragStartCell: (SelectedCell & { cellId: string }) | null
  dynamicColumnIds: string[]
  isDraggingActive: boolean
}

export type SelectedCell = {
  columnId: string
  rowId: string
  endDate?: string
  startDate?: string
  rowData: ObjectDataRecord
  events: ObjectDataRecord[]
}

export type TimelineCellContextMenuData = {
  event: MouseEvent<HTMLDivElement>
  action: ActionRow
  params: GridRenderCellParams
  cellItem?: ObjectDataRecord
  cellContextRef: MutableRefObject<TimelineCellContext>
  isOpenedFromSelectedCell: boolean
}
export type HandleTimelineCellContextMenu = (data: TimelineCellContextMenuData) => void

export type TimelineChangeSelectedCellsData = {
  cellId: string
  objectXItems: ObjectDataRecord[]
  columnId: string
  rowId: string
  endDate: string
  startDate: string
  rowData: ObjectDataRecord
}

export type HandleTimelineChangeSelectedCells = (data: TimelineChangeSelectedCellsData) => void

export type TimelineToggleSelectedEventData = {
  cellId: string
  event: ObjectDataRecord
  pkFieldName?: string
  columnId: string
  rowId: string
  endDate: string
  startDate: string
  rowData: ObjectDataRecord
}

export type HandleTimelineToggleSelectedEvent = (data: TimelineToggleSelectedEventData) => void

export type TimelineActionMeta = {
  scriptAdditionalInfo?: {
    columnFields: Variable['type']
    objectXItemFields: Variable['type']
    rowData: Variable['type']
  }
}
